import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { scrollIntoViewWithinContainer } from '@/shared/lib';

/** Query-параметр адреса карточки диалога с правленым элементом заключения */
export const EVALUATION_TARGET_PARAM = 'evaluationTarget';

/** Подсветка элемента, к которому привёл переход из «Обратной связи» */
export const EVALUATION_TARGET_HIGHLIGHT_CLASS = 'ring-2 ring-primary/50 bg-primary/5';

/** Ключ правленого элемента заключения: балл блока или комментарий правила */
export const buildEvaluationTargetKey = (target: 'block' | 'rule', targetId: number): string => `${target}-${targetId}`;

/** Наведение карточки диалога на правленый элемент заключения */
export type EvaluationTarget = {
  /** Ключ элемента, к которому ведёт переход; `null` — переход не из «Обратной связи» */
  targetKey: string | null;
  /** Ref элемента цели: по его появлению в разметке заключение прокручивается к нему */
  attachTarget: (element: HTMLElement | null) => void;
  /** Ref области прокрутки заключения — её и двигаем к элементу цели */
  viewportRef: RefObject<HTMLDivElement | null>;
};

/**
 * Правленый элемент заключения, к которому ведёт переход из раздела «Обратная связь».
 *
 * Элемент цели приходит ref-ом: строки заключения строит другой компонент, и к моменту, когда
 * цель оказывается в разметке, эффект по готовому списку уже отработал бы. Сама прокрутка идёт
 * следом отдельным проходом и по явно переданной области: полоса прокрутки заключения включается
 * не в том же проходе, что и его строки, а до этого область прокручиваемой не выглядит — прокрутка
 * прямо из ref-а не сдвинула бы ничего, и от перехода оставалась бы одна подсветка.
 *
 * Неизвестный элемент просто не встречается среди строк — переход остаётся обычным открытием
 * заключения.
 */
export const useEvaluationTarget = (): EvaluationTarget => {
  const [searchParams] = useSearchParams();
  const targetKey = searchParams.get(EVALUATION_TARGET_PARAM);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const shownKeyRef = useRef<string | null>(null);

  const attachTarget = useCallback((element: HTMLElement | null) => {
    if (element) setTargetElement(element);
  }, []);

  useEffect(() => {
    if (!targetKey || !targetElement || shownKeyRef.current === targetKey) return;
    shownKeyRef.current = targetKey;
    scrollIntoViewWithinContainer(targetElement, {
      align: 'center',
      behavior: 'smooth',
      container: viewportRef.current,
    });
  }, [targetElement, targetKey]);

  return { targetKey, attachTarget, viewportRef };
};
